import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import MatchScoreRing from "@/components/MatchScoreRing";
import { toast } from "sonner";
import { Check, Heart, ShieldAlert, Lock } from "lucide-react";
import StatusBadges from "@/components/StatusBadges";

export default function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [verified, setVerified] = useState(true);

  const load = async () => {
    const [op, prof] = await Promise.all([
      api.get("/coach/opportunities"),
      api.get("/coach/profile").catch(() => ({ data: {} })),
    ]);
    setOpps(op.data);
    setVerified((prof.data?.verification_status || "unverified") === "verified");
    setLoading(false);
  };

  useEffect(() => { load().catch(() => setLoading(false)); }, []);

  const express = async (jobId, isInterested) => {
    setBusyId(jobId);
    try {
      if (isInterested) {
        await api.delete(`/coach/interests/${jobId}`);
        toast.success("Interest withdrawn. Programs will no longer see you for this job.");
      } else {
        await api.post("/coach/interests", { job_id: jobId });
        toast.success("Interest expressed. You're now visible to this program for this role.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="opportunities-page">
        <div className="text-xs tracking-widest text-blaze">CONSENT-FIRST OPPORTUNITIES</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">YOUR OPPORTUNITIES</h1>
        <p className="text-neutral-400 mt-2 max-w-2xl">Ranked by MatchEngineAgent. You&apos;re only visible to programs for jobs you&apos;ve explicitly EXPRESSED INTEREST in.</p>

        {!verified && (
          <div className="mt-6 border border-blaze p-4 bg-[#1A0A0A] flex items-start gap-3" data-testid="verify-banner">
            <ShieldAlert className="text-blaze shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="heading text-lg">VERIFY YOUR PROFILE FIRST</div>
              <p className="text-neutral-300 text-sm mt-1">Programs trust verified profiles 3× more. Confirm your data is accurate before expressing interest in roles.</p>
            </div>
            <Link to="/coach/profile" data-testid="verify-cta" className="bg-blaze text-white heading text-sm px-4 py-2 hover:glow-blaze whitespace-nowrap">VERIFY NOW</Link>
          </div>
        )}

        {loading ? <div className="mt-8 heading text-2xl">LOADING…</div> : opps.length === 0 ? (
          <div className="mt-8 border border-dashed border-neutral-800 p-10 text-neutral-500">No openings match yet. Set your alert criteria so we can notify you when matches appear.</div>
        ) : (
          <div className="mt-8 space-y-4">
            {opps.map(({ job, match, interested, paywalled, facility_fit }) => (
              <div
                key={match.id}
                data-testid={`opp-row-${job.id}`}
                className={`border bg-appcard p-6 ${paywalled ? "border-volt/40" : interested ? "border-volt" : "border-neutral-800"} transition-colors`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4">
                    <MatchScoreRing score={match.score} size={90} stroke={8} />
                    <div className="text-mono text-xs text-neutral-500">CIR {match.rationale?.qbr?.score ?? "—"}</div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="text-xs tracking-widest text-neutral-500 flex items-center gap-3 flex-wrap">
                      <span>{job.gender?.toUpperCase()} · {job.sport?.toUpperCase()} · {job.division || job.level}</span>
                      {job.source === "usalacrosse.com" && !paywalled && <span className="bg-blaze text-white px-2 py-0.5 text-[9px] tracking-widest">USA LACROSSE BOARD</span>}
                      {job.anonymous_listing && <span className="bg-volt/20 border border-volt text-volt px-2 py-0.5 text-[9px] tracking-widest" data-testid={`anon-badge-${job.id}`}>ANONYMOUS · HELD WITHIN COACHCONNECT</span>}
                      <StatusBadges adFounder={job.poster_badges?.ad_founder} />
                      {facility_fit && (
                        <span data-testid={`fac-fit-${job.id}`} className={`px-2 py-0.5 text-[9px] tracking-widest border ${facility_fit.score >= 4 ? "border-volt text-volt" : facility_fit.score >= 2 ? "border-neutral-600 text-neutral-300" : "border-neutral-800 text-neutral-500"}`} title={facility_fit.label + (facility_fit.covered?.length ? ` · Covers: ${facility_fit.covered.join(", ")}` : "")}>
                          🏟 FACILITY FIT {facility_fit.score}/5
                        </span>
                      )}
                    </div>
                    {paywalled ? (
                      <div className="heading text-2xl mt-1 flex items-center gap-2 text-volt" data-testid={`opp-paywall-${job.id}`}><Lock size={20} /> {job.title}</div>
                    ) : (
                      <Link to={`/jobs/${job.id}`} className="heading text-2xl mt-1 block hover:text-blaze" data-testid={`opp-title-${job.id}`}>{job.title}</Link>
                    )}
                    {paywalled ? (
                      <div className="text-volt text-sm mt-1 heading tracking-widest">PROGRAM DETAILS LOADING</div>
                    ) : (
                      <div className="text-neutral-400 text-sm">
                        {job.program_name}{job.salary_band ? ` · $${job.salary_band}` : ""} · {job.region || job.location}
                        {(String(job.job_type || "").toLowerCase().includes("part") || String(job.job_type || "").toLowerCase().includes("stipend")) && (
                          <span data-testid={`opp-parttime-badge-${job.id}`} className="ml-2 text-[10px] tracking-widest text-volt border border-volt/40 px-1.5 py-0.5">
                            PART-TIME{job.stipend ? ` · ${job.stipend}` : " · STIPEND"}
                          </span>
                        )}
                      </div>
                    )}
                    <div className={`mt-3 text-sm ${paywalled ? "text-neutral-500 italic" : "text-neutral-300"}`}>
                      {paywalled ? "Possible match in your wheelhouse — full details are free for coaches." : match.rationale?.summary}
                    </div>
                  </div>
                  <div className="md:col-span-3 flex flex-col gap-2">
                    {paywalled ? (
                      <Link to={`/jobs/${job.id}`} data-testid={`upgrade-cta-${job.id}`} className="bg-volt text-white heading text-base px-4 py-3 hover:glow-volt transition-all flex items-center justify-center gap-2">VIEW DETAILS</Link>
                    ) : (
                      <button
                        onClick={() => express(job.id, interested)}
                        disabled={busyId === job.id}
                        data-testid={`${interested ? "withdraw" : "express"}-interest-${job.id}`}
                        className={`heading text-base px-4 py-3 transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
                          interested ? "bg-volt text-white" : "bg-blaze text-white hover:glow-blaze"
                        }`}
                      >
                        {interested ? (<><Check size={16} /> INTEREST EXPRESSED</>) : (<><Heart size={16} /> EXPRESS INTEREST</>)}
                      </button>
                    )}
                    {!paywalled && <Link to={`/jobs/${job.id}`} className="text-xs text-volt uppercase tracking-widest text-center py-2 border border-neutral-800 hover:border-volt">VIEW DETAILS</Link>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
